import "./NewToBrand.scss";
import React, { useEffect, useState } from "react";
import * as R from "ramda";
import { AmcLambdaFetch, awaitJSON } from "../../../utils/fetch-utils";
import { useSetError } from "../../../redux/modals";
import { useCompanyInfo } from "../../../redux/company";
import { BPMTable, FullPageSpinner, Page } from "../../../Components";
import {
  formatDate,
  MonthPicker,
  startDateRange,
} from "../../../Components/MonthPicker/MonthPicker";
import {
  getNtbTableHeaders,
  sanitizeBehaviorData,
  sanitizeOverviewData,
  sanitizeOverviewGraph,
  sanitizeNtbVsRepeatBehavior,
  GetAmcNtbResponse,
} from "./NewToBrandConfig";

import DataCardRow from "./DataCardRow";
import LineChart from "../../AMCLineChart";
import BarChart from "../../AMCStackedBarChart";
import { RouteComponentProps } from "@reach/router";
import { AMC_TEST_INSTANCE_ID } from "../../Insights";

/*eslint-disable no-empty-pattern*/
const NewToBrand = ({}: RouteComponentProps): JSX.Element => {
  const setError = useSetError();
  let { cid, amc_instance_id: amcInstanceId } = useCompanyInfo();
  if (cid === "tinuititest") {
    amcInstanceId = AMC_TEST_INSTANCE_ID;
  }
  // Previous month reports not prepared until the 15th of current month
  const monthsBack = new Date().getDate() < 15 ? 2 : 1;
  const [startDateSelection, updateStartDateSelection] = useState(startDateRange(monthsBack));
  const [endDateSelection, updateEndDateSelection] = useState(startDateRange(monthsBack));
  const [dateOptions, updateDateOptions] = useState([{ value: "" }]);
  const [dateRange, updateDateRange] = useState({
    endDate: formatDate(endDateSelection, "end"),
    startDate: formatDate(startDateSelection, "start"),
  });

  const [ntbData, setNtbData] = useState<GetAmcNtbResponse | null>(null);
  const [maxValues, setMaxValues] = useState({
    maxNtbRate: 0,
    maxNtbRepeatRate: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNtbData = async () => {
      if (!amcInstanceId) {
        return;
      }
      try {
        setLoading(true);
        const response = await AmcLambdaFetch("/getNtbReport", {
          params: {
            amcInstanceId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            duration: "monthly",
          },
        });
        const json: GetAmcNtbResponse = await awaitJSON(response);
        const sanitized: GetAmcNtbResponse = {
          ...json,
          behaviorByGroup: sanitizeBehaviorData(json.behaviorByGroup),
          overview: sanitizeOverviewData(json.overview),
          overviewGraph: sanitizeOverviewGraph(json.overviewGraph),
          ntbVsRepeatBehavior: sanitizeNtbVsRepeatBehavior(json.ntbVsRepeatBehavior),
        };
        setNtbData(sanitized);

        setMaxValues({
          maxNtbRate: Math.max(...json.behaviorByGroup.map(d => d.ntbRate)),
          maxNtbRepeatRate: Math.max(...json.behaviorByGroup.map(d => d.ntbRepeatRate)),
        });
      } catch (error) {
        setError({
          title: "Error fetching New to Brand data",
          message: "There was an error fetching the New to Brand data. Please try again later.",
        });
        setNtbData(null);
      } finally {
        setLoading(false);
      }
    };
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      setError({
        title: "Invalid Date Range",
        message: "Please select a valid date range where the start date is before the end date.",
      });
      const start = startDateRange(monthsBack + 2);
      const end = startDateRange(monthsBack);
      updateStartDateSelection(start);
      updateEndDateSelection(end);
      updateDateRange({
        startDate: formatDate(start, "start"),
        endDate: formatDate(end, "end"),
      });
      return;
    }
    fetchNtbData();
  }, [amcInstanceId, dateRange, monthsBack, setError]);

  const tableData = R.sort(R.descend(R.prop("customersConverted")), ntbData?.behaviorByGroup || []);

  const costCardData = ntbData?.overview.cost;
  const revenueCardData = ntbData?.overview.revenue;

  useEffect(() => {
    if (ntbData?.availableFilters.months) {
      const months = ntbData?.availableFilters.months;
      const options = months?.map(m => {
        return { value: m };
      });

      updateDateOptions(options);
    }
  }, [ntbData?.availableFilters.months]);

  // To display graph in % we have to clone data and add percent attr for each data point displayed.
  const purchasesGraphData = R.clone(ntbData?.ntbVsRepeatBehavior.purchases);
  purchasesGraphData?.forEach((d, index) => {
    const total = d.ntbCustomers + d.repeatCustomers;
    const ntbPercent = (d.ntbCustomers / total) * 100;
    const repeatPercent = (d.repeatCustomers / total) * 100;
    purchasesGraphData[index].ntbCustomersPercent = ntbPercent;
    purchasesGraphData[index].repeatCustomersPercent = repeatPercent;
  });

  const customersGraphData = R.clone(ntbData?.ntbVsRepeatBehavior.customers);
  customersGraphData?.forEach((d, index) => {
    const total = d.ntbCustomers + d.ntbRepeatCustomers;
    const ntbPercent = (d.ntbCustomers / total) * 100;
    const repeatPercent = (d.ntbRepeatCustomers / total) * 100;
    customersGraphData[index].ntbCustomersPercent = ntbPercent;
    customersGraphData[index].ntbRepeatCustomersPercent = repeatPercent;
  });

  return (
    <Page minHeight="600px" pageType="AMC New To Brand" title="AMC New To Brand">
      {loading && <FullPageSpinner />}
      <div className="ntb-page">
        <div className="ntb-page-header">
          <div className="back-arrow"></div>
          <MonthPicker
            dateOptions={dateOptions}
            endDateSelection={endDateSelection}
            startDateSelection={startDateSelection}
            updateDateRange={updateDateRange}
            updateEndDateSelection={updateEndDateSelection}
            updateStartDateSelection={updateStartDateSelection}
          />
        </div>
        <div className="row-header">
          <div className="row-header-text">NTB Cost</div>
          <div className="row-header-text">NTB Revenue</div>
        </div>
        {costCardData && revenueCardData && (
          <DataCardRow costCardData={costCardData} revenueCardData={revenueCardData} />
        )}

        <div className="ntb-row">
          <div className="basic-card">
            <div className="graph-wrapper">
              <LineChart
                data={ntbData?.overviewGraph || []}
                dataKey="ntbCpa"
                lineColor="#63da66"
                tooltipKey="NTB CPA"
              />
            </div>
          </div>
          <div className="basic-card">
            <div className="graph-wrapper">
              <LineChart
                data={ntbData?.overviewGraph || []}
                dataKey="ntbRoas"
                lineColor="#FF6C95"
                tooltipKey="NTB ROAS"
              />
            </div>
          </div>
        </div>

        <div className="row-header">NTB vs Repeat Customer Behavior</div>
        <div className="ntb-row">
          <div className="basic-card bar-chart">
            <BarChart
              data={purchasesGraphData || []}
              dataKeys={["ntbCustomers", "repeatCustomers"]}
              percentKeys={["ntbCustomersPercent", "repeatCustomersPercent"]}
              fill={{ ntbCustomers: "#6070FF", repeatCustomers: "#01b6cb" }}
              dataLabel={{
                ntbCustomersPercent: "NTB Customers",
                repeatCustomersPercent: "Repeat Customers",
              }}
            />
          </div>
          <div className="basic-card bar-chart">
            <BarChart
              data={customersGraphData || []}
              dataKeys={["ntbCustomers", "ntbRepeatCustomers"]}
              percentKeys={["ntbCustomersPercent", "ntbRepeatCustomersPercent"]}
              fill={{ ntbCustomers: "#ffa800", ntbRepeatCustomers: "#f66e03" }}
              dataLabel={{
                ntbCustomersPercent: "NTB Customers",
                ntbRepeatCustomersPercent: "NTB Repeat Customers",
              }}
            />
          </div>
        </div>
        <div className="row-header">NTB Behavior by Group</div>
        <div className="ntb-row">
          <div className="basic-card">
            <div className="table-wrapper">
              <BPMTable
                data={tableData}
                filterBar={false}
                headers={getNtbTableHeaders(maxValues)}
                noRowsRenderer={() => <div className="noFailures">No data to show</div>}
                pagination={true}
              />
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default NewToBrand;
